import React, { useState, useEffect } from 'react';
import { 
  StyleSheet, 
  View, 
  Text, 
  ScrollView, 
  Image, 
  TouchableOpacity, 
  Alert, 
  ActivityIndicator,
  FlatList
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { User } from '../../types';
import { colors, spacing, typography } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useTheme } from '../../context/ThemeContext';

type Props = NativeStackScreenProps<MainStackParamList, 'FriendProfile'>;

interface FriendUserData extends User {
  shareHeight?: boolean;
  shareWeight?: boolean;
  shareProgress?: boolean;
}

interface ExerciseProgress {
  exerciseId: string;
  exerciseName: string;
  weight: number;
  date: Date;
}

enum FriendshipStatus {
  LOADING = "loading",
  NOT_FRIEND = "not_friend",
  FRIEND = "friend",
  PENDING = "pending", // Solicitud enviada
  REQUESTED = "requested" // Solicitud recibida
}

const FriendProfileScreen = ({ route, navigation }: Props) => {
  const { friendId } = route.params;
  const [userData, setUserData] = useState<FriendUserData | null>(null);
  const [progressData, setProgressData] = useState<ExerciseProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [progressLoading, setProgressLoading] = useState(true);
  const [friendshipStatus, setFriendshipStatus] = useState<FriendshipStatus>(FriendshipStatus.LOADING);
  const [sendingRequest, setSendingRequest] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    fetchFriendData();
    checkFriendshipStatus();
  }, [friendId]);

  const fetchFriendData = async () => {
    setLoading(true);
    try {
      // Obtener datos del amigo
      const userRef = doc(firestore, 'users', friendId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('No se encontró el perfil de usuario');
      }
      
      const friendData = userDoc.data() as FriendUserData;
      setUserData({
        ...friendData,
        id: friendId
      });

      // Si el amigo comparte su progreso, obtener sus datos de ejercicios
      if (friendData.shareProgress) {
        fetchFriendProgress(friendId);
      } else {
        setProgressLoading(false);
      }
    } catch (error) {
      console.error('Error al obtener datos del amigo:', error);
      Alert.alert('Error', 'No se pudieron cargar los datos del perfil');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const checkFriendshipStatus = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }

      // Obtener datos del usuario actual
      const userRef = doc(firestore, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('No se encontró el perfil de usuario');
      }
      
      const userData = userDoc.data() as User;

      // Verificar si ya es amigo
      if (userData.friends && userData.friends.includes(friendId)) {
        setFriendshipStatus(FriendshipStatus.FRIEND);
        return;
      }

      // Verificar si hay solicitud pendiente enviada
      if (userData.pendingFriendRequests && userData.pendingFriendRequests.includes(friendId)) {
        setFriendshipStatus(FriendshipStatus.PENDING);
        return;
      }

      // Verificar si hay solicitud recibida
      if (userData.friendRequests && userData.friendRequests.includes(friendId)) {
        setFriendshipStatus(FriendshipStatus.REQUESTED);
        return;
      }

      // Si no es amigo y no hay solicitudes
      setFriendshipStatus(FriendshipStatus.NOT_FRIEND);
    } catch (error) {
      console.error('Error al verificar estado de amistad:', error);
      setFriendshipStatus(FriendshipStatus.NOT_FRIEND);
    }
  };

  const fetchFriendProgress = async (userId: string) => {
    setProgressLoading(true);
    try {
      // Consultar los últimos registros de progreso
      const progressRef = collection(firestore, 'progress');
      const q = query(
        progressRef,
        where('userId', '==', userId),
        orderBy('date', 'desc'),
        limit(10)
      );
      
      const querySnapshot = await getDocs(q);
      const progress: ExerciseProgress[] = [];
      
      // Obtener IDs de ejercicios
      const exerciseIds = new Set<string>();
      querySnapshot.forEach(doc => {
        const data = doc.data();
        exerciseIds.add(data.exerciseId);
      });
      
      // Obtener nombres de ejercicios
      const exerciseNames: Record<string, string> = {};
      await Promise.all(
        Array.from(exerciseIds).map(async (exerciseId) => {
          const exerciseRef = doc(firestore, 'exercises', exerciseId);
          const exerciseDoc = await getDoc(exerciseRef);
          
          if (exerciseDoc.exists()) {
            exerciseNames[exerciseId] = exerciseDoc.data().name;
          }
        })
      );
      
      // Preparar los datos de progreso
      querySnapshot.forEach(doc => {
        const data = doc.data();
        progress.push({
          exerciseId: data.exerciseId,
          exerciseName: exerciseNames[data.exerciseId] || 'Ejercicio desconocido',
          weight: data.weight,
          date: data.date.toDate()
        });
      });
      
      setProgressData(progress);
    } catch (error) {
      console.error('Error al obtener progreso del amigo:', error);
    } finally {
      setProgressLoading(false);
    }
  };

  const sendFriendRequest = async () => {
    setSendingRequest(true);
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }

      // Obtener referencia a documentos
      const userRef = doc(firestore, 'users', userId);
      const friendRef = doc(firestore, 'users', friendId);

      // Actualizar pendingFriendRequests del usuario actual
      await updateDoc(userRef, {
        pendingFriendRequests: arrayUnion(friendId)
      });

      // Actualizar friendRequests del amigo
      await updateDoc(friendRef, {
        friendRequests: arrayUnion(userId)
      });

      // Actualizar estado
      setFriendshipStatus(FriendshipStatus.PENDING);
      Alert.alert('Solicitud Enviada', `Se ha enviado una solicitud de amistad a ${userData?.firstName}`);
    } catch (error) {
      console.error('Error al enviar solicitud de amistad:', error);
      Alert.alert('Error', 'No se pudo enviar la solicitud de amistad');
    } finally {
      setSendingRequest(false);
    }
  };

  const cancelFriendRequest = async () => {
    setSendingRequest(true);
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }

      // Obtener referencia a documentos
      const userRef = doc(firestore, 'users', userId);
      const friendRef = doc(firestore, 'users', friendId);

      // Actualizar documentos para eliminar la solicitud pendiente
      await updateDoc(userRef, {
        pendingFriendRequests: arrayRemove(friendId)
      });

      await updateDoc(friendRef, {
        friendRequests: arrayRemove(userId)
      });

      // Actualizar estado
      setFriendshipStatus(FriendshipStatus.NOT_FRIEND);
      Alert.alert('Solicitud Cancelada', 'La solicitud de amistad ha sido cancelada');
    } catch (error) {
      console.error('Error al cancelar solicitud de amistad:', error);
      Alert.alert('Error', 'No se pudo cancelar la solicitud');
    } finally {
      setSendingRequest(false);
    }
  };

  const renderProgressItem = ({ item }: { item: ExerciseProgress }) => {
    const dateFormatted = new Date(item.date).toLocaleDateString();
    
    return (
      <Card style={styles.progressItem}>
        <View style={styles.progressHeader}>
          <Text style={[styles.exerciseName, { color: theme.colors.text }]}>
            {item.exerciseName}
          </Text>
          <Text style={[styles.progressDate, { color: theme.colors.grey.dark }]}>
            {dateFormatted}
          </Text>
        </View>
        
        <View style={styles.progressContent}>
          <View style={styles.weightContainer}>
            <Text style={[styles.weightValue, { color: theme.colors.primary }]}>
              {item.weight}
            </Text>
            <Text style={[styles.weightUnit, { color: theme.colors.primary }]}>kg</Text>
          </View>
        </View>
      </Card>
    );
  };

  const renderFriendshipButton = () => {
    switch (friendshipStatus) {
      case FriendshipStatus.NOT_FRIEND:
        return (
          <Button 
            title="Enviar solicitud de amistad" 
            onPress={sendFriendRequest} 
            type="primary"
            loading={sendingRequest}
            style={styles.friendshipButton}
          />
        );
      case FriendshipStatus.PENDING:
        return (
          <View style={styles.buttonGroup}>
            <Button 
              title="Cancelar solicitud" 
              onPress={cancelFriendRequest} 
              type="outline"
              loading={sendingRequest}
              style={styles.friendshipButton}
            />
            <Text style={[styles.statusText, { color: theme.colors.grey.dark }]}>
              Solicitud pendiente
            </Text>
          </View>
        );
      case FriendshipStatus.REQUESTED:
        return (
          <Button 
            title="Aceptar solicitud" 
            onPress={() => navigation.navigate('FriendManagement')} 
            type="primary"
            style={styles.friendshipButton}
          />
        );
      case FriendshipStatus.FRIEND:
      case FriendshipStatus.LOADING:
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  if (!userData) {
    return (
      <View style={[styles.errorContainer, { backgroundColor: theme.colors.background }]}>
        <Ionicons name="alert-circle-outline" size={64} color={theme.colors.error} />
        <Text style={[styles.errorText, { color: theme.colors.error }]}>
          No se pudo cargar el perfil
        </Text>
        <TouchableOpacity 
          style={styles.backButton} 
          onPress={() => navigation.goBack()}
        >
          <Text style={[styles.backButtonText, { color: theme.colors.primary }]}>
            Volver
          </Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.mainContainer}>
      <ScrollView 
        style={[styles.scrollView, { backgroundColor: theme.colors.background }]}
        contentContainerStyle={styles.scrollViewContent}
      >
        <View style={styles.container}>
          {/* Cabecera de perfil */}
          <View style={styles.header}>
            <View style={styles.profileImageContainer}>
              {userData.profilePhoto ? (
                <Image 
                  source={{ uri: userData.profilePhoto }} 
                  style={styles.profileImage} 
                />
              ) : (
                <View style={[
                  styles.profileImagePlaceholder, 
                  { backgroundColor: theme.colors.primary }
                ]}>
                  <Text style={styles.profileInitials}>
                    {userData.firstName?.charAt(0)}{userData.lastName?.charAt(0)}
                  </Text>
                </View>
              )}
            </View>
            
            <Text style={[styles.username, { color: theme.colors.grey.dark }]}>
              @{userData.username}
            </Text>
            <Text style={[styles.name, { color: theme.colors.text }]}>
              {userData.firstName} {userData.lastName}
            </Text>
          </View>

          {/* Tarjeta de medidas corporales - mostrada solo si el amigo la comparte */}
          {(userData.shareHeight || userData.shareWeight) && (
            <Card style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
                  Medidas Corporales
                </Text>
              </View>
              
              <View style={styles.measurementsContainer}>
                {userData.shareHeight && userData.height && (
                  <View style={styles.measurementItem}>
                    <Text style={[styles.measurementLabel, { color: theme.colors.grey.dark }]}>
                      Altura
                    </Text>
                    <Text style={[styles.measurementValue, { color: theme.colors.text }]}>
                      {userData.height} cm
                    </Text>
                  </View>
                )}
                
                {userData.shareWeight && userData.weight && (
                  <View style={styles.measurementItem}>
                    <Text style={[styles.measurementLabel, { color: theme.colors.grey.dark }]}>
                      Peso
                    </Text>
                    <Text style={[styles.measurementValue, { color: theme.colors.text }]}>
                      {userData.weight} kg
                    </Text>
                  </View>
                )}
                
                {userData.shareHeight && userData.shareWeight && userData.height && userData.weight && (
                  <View style={styles.measurementItem}>
                    <Text style={[styles.measurementLabel, { color: theme.colors.grey.dark }]}>
                      IMC
                    </Text>
                    <Text style={[styles.measurementValue, { color: theme.colors.text }]}>
                      {(userData.weight / ((userData.height / 100) ** 2)).toFixed(1)}
                    </Text>
                  </View>
                )}
              </View>
            </Card>
          )}

          {/* Tarjeta de progreso - mostrada solo si el amigo la comparte */}
          {userData.shareProgress && (
            <Card style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
                  Progreso Reciente
                </Text>
              </View>
              
              {progressLoading ? (
                <View style={styles.progressLoadingContainer}>
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                </View>
              ) : progressData.length > 0 ? (
                <FlatList
                  data={progressData}
                  renderItem={renderProgressItem}
                  keyExtractor={(item, index) => `${item.exerciseId}-${index}`}
                  scrollEnabled={false}
                  nestedScrollEnabled={true}
                />
              ) : (
                <View style={styles.emptyProgressContainer}>
                  <Text style={[styles.emptyText, { color: theme.colors.grey.dark }]}>
                    No hay datos de progreso para mostrar
                  </Text>
                </View>
              )}
            </Card>
          )}
          
          {/* Si no comparte nada */}
          {!userData.shareHeight && !userData.shareWeight && !userData.shareProgress && (
            <Card style={styles.card}>
              <View style={styles.emptySharesContainer}>
                <Ionicons name="lock-closed" size={48} color={theme.colors.grey.medium} />
                <Text style={[styles.emptySharesText, { color: theme.colors.text }]}>
                  Este usuario no comparte información adicional
                </Text>
              </View>
            </Card>
          )}
        </View>
      </ScrollView>

      {/* Footer con botón de amistad si no es amigo */}
      {friendshipStatus !== FriendshipStatus.FRIEND && friendshipStatus !== FriendshipStatus.LOADING && (
        <View style={[styles.footer, { 
          backgroundColor: theme.colors.card, 
          borderTopColor: theme.colors.grey.light 
        }, theme.shadows.medium]}>
          <View style={styles.footerContent}>
            {renderFriendshipButton()}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  container: {
    padding: spacing.lg,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  errorText: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    marginTop: spacing.md,
    textAlign: 'center',
  },
  backButton: {
    marginTop: spacing.lg,
    padding: spacing.md,
  },
  backButtonText: {
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
  },
  header: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  profileImageContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    marginBottom: spacing.md,
    overflow: 'hidden',
  },
  profileImage: {
    width: '100%',
    height: '100%',
  },
  profileImagePlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileInitials: {
    fontSize: typography.fontSize.xxl,
    fontWeight: 'bold',
    color: colors.white,
  },
  username: {
    fontSize: typography.fontSize.md,
    marginBottom: spacing.xs,
  },
  name: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
  },
  card: {
    marginBottom: spacing.lg,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  cardTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
  },
  measurementsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  measurementItem: {
    width: '48%',
    marginBottom: spacing.md,
  },
  measurementLabel: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.xs,
  },
  measurementValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
  },
  progressLoadingContainer: {
    padding: spacing.lg,
    alignItems: 'center',
  },
  progressItem: {
    marginBottom: spacing.sm,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  exerciseName: {
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
  },
  progressDate: {
    fontSize: typography.fontSize.sm,
  },
  progressContent: {
    flexDirection: 'row',
  },
  weightContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  weightValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
  },
  weightUnit: {
    marginLeft: spacing.xs,
    fontSize: typography.fontSize.sm,
  },
  emptyProgressContainer: {
    padding: spacing.lg,
    alignItems: 'center',
  },
  emptySharesContainer: {
    padding: spacing.xl,
    alignItems: 'center',
  },
  emptySharesText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.md,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: typography.fontSize.md,
    textAlign: 'center',
  },
  footer: {
    padding: spacing.md,
    borderTopWidth: 1,
  },
  footerContent: {
    alignItems: 'center',
  },
  friendshipButton: {
    minWidth: 200,
  },
  buttonGroup: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusText: {
    marginLeft: spacing.md,
    fontSize: typography.fontSize.md,
  },
});

export default FriendProfileScreen; 